import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Title,
  Text,
  Grid,
  Card,
  Badge,
  Button,
  TextInput,
  Switch,
} from '@tremor/react';
import { Settings as SettingsIcon, Database, Server } from 'lucide-react';
import { KPICard } from '@/components/KPICard';
import { LoadingSkeleton } from '@/components/LoadingSkeleton';
import { api, extractErrorMessage } from '@/lib/api';

interface FeatureFlag {
  name: string;
  enabled: boolean;
  raw_value: string | null;
  description: string;
}

interface DbStats {
  version: string | null;
  active_connections: number;
  idle_connections: number;
  db_size_bytes: number | null;
  largest_tables: Array<{ name: string; bytes: number; pretty: string }>;
}

interface WorkerSummary {
  queue_pending: number;
  queue_processing: number;
  queue_validating: number;
  queue_failed_24h: number;
  queue_completed_24h: number;
}

interface RescueSummary {
  table_present: boolean;
  events_24h: number;
  healed_24h: number;
  escalated_24h: number;
}

interface SystemInfo {
  server_time: string;
  feature_flags: FeatureFlag[];
  db: DbStats;
  worker: WorkerSummary;
  rescue: RescueSummary;
}

interface SystemSetting {
  key: string;
  value: string;
  value_type: string;
  category: string;
  description: string | null;
  updated_at: string | null;
  updated_by: string | null;
}

function fmtBytes(n: number | null): string {
  if (n === null) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

export function SystemPage() {
  const qc = useQueryClient();
  const [bannerDraft, setBannerDraft] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const info = useQuery<SystemInfo>({
    queryKey: ['system.info'],
    queryFn: async () => (await api().get('/system/info')).data,
    refetchInterval: 30_000,
  });

  const settings = useQuery<SystemSetting[]>({
    queryKey: ['system.settings'],
    queryFn: async () => (await api().get('/system/settings')).data,
    refetchInterval: 30_000,
  });

  const upsertSetting = useMutation({
    mutationFn: async (vars: { key: string; value: string; value_type?: string }) => {
      const r = await api().put(`/system/settings/${vars.key}`, {
        value: vars.value,
        value_type: vars.value_type,
      });
      return r.data;
    },
    onSuccess: () => {
      setError(null);
      qc.invalidateQueries({ queryKey: ['system.settings'] });
    },
    onError: (err) => setError(extractErrorMessage(err, 'Falha ao salvar')),
  });

  if (info.isLoading) return <LoadingSkeleton />;
  if (!info.data) return <Text>Failed to load system info.</Text>;

  const i = info.data;
  const maintenanceSettings = (settings.data ?? []).filter(
    (s) => s.category === 'maintenance',
  );

  const findSetting = (key: string) =>
    maintenanceSettings.find((s) => s.key === key);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <Title>System</Title>
          <Text>Status operacional do JusQuant + chaves de manutenção.</Text>
        </div>
        <Badge color="slate" icon={SettingsIcon}>
          server: {new Date(i.server_time).toLocaleString('pt-BR')}
        </Badge>
      </div>

      {/* Snapshot KPIs */}
      <Grid numItemsMd={4} className="gap-4">
        <KPICard
          label="Fila pendente"
          value={i.worker.queue_pending.toString()}
          hint={`${i.worker.queue_processing} em processamento`}
        />
        <KPICard
          label="Concluídos 24h"
          value={i.worker.queue_completed_24h.toString()}
          hint={`${i.worker.queue_failed_24h} falharam`}
        />
        <KPICard
          label="DB conexões"
          value={(i.db.active_connections + i.db.idle_connections).toString()}
          hint={`${i.db.active_connections} ativas / ${i.db.idle_connections} idle`}
        />
        <KPICard
          label="Rescue 24h"
          value={
            i.rescue.table_present
              ? `${i.rescue.healed_24h}/${i.rescue.events_24h}`
              : 'off'
          }
          hint={
            i.rescue.table_present
              ? `${i.rescue.escalated_24h} escalated`
              : 'rescue_event não criada'
          }
        />
      </Grid>

      {/* Maintenance switches */}
      <Card>
        <div className="flex items-center justify-between">
          <Title>Manutenção</Title>
          <Server className="h-4 w-4 text-slate-500" />
        </div>
        {error && (
          <div className="mt-2 rounded-md bg-red-950/40 p-2 text-sm text-red-300">
            {error}
          </div>
        )}
        <div className="mt-4 space-y-4 text-sm">
          {[
            'maintenance.read_only_mode',
            'maintenance.signup_disabled',
          ].map((k) => {
            const s = findSetting(k);
            if (!s) return null;
            const enabled = s.value.toLowerCase() === 'true' || s.value === '1';
            return (
              <div
                key={k}
                className="flex items-center justify-between border-b border-slate-800/60 pb-3"
              >
                <div>
                  <code className="text-blue-300">{k}</code>
                  <div className="text-xs text-slate-500">{s.description}</div>
                </div>
                <Switch
                  checked={enabled}
                  onChange={(checked) =>
                    upsertSetting.mutate({
                      key: k,
                      value: checked ? 'true' : 'false',
                      value_type: 'bool',
                    })
                  }
                />
              </div>
            );
          })}

          {(() => {
            const s = findSetting('maintenance.banner_message');
            if (!s) return null;
            const draft = bannerDraft ?? s.value;
            return (
              <div className="space-y-2">
                <code className="text-blue-300">maintenance.banner_message</code>
                <div className="text-xs text-slate-500">{s.description}</div>
                <div className="flex gap-2">
                  <TextInput
                    value={draft}
                    onChange={(e) => setBannerDraft(e.target.value)}
                    placeholder="(vazio = sem banner)"
                  />
                  <Button
                    onClick={() =>
                      upsertSetting.mutate({
                        key: 'maintenance.banner_message',
                        value: draft,
                        value_type: 'string',
                      })
                    }
                    disabled={draft === s.value}
                  >
                    Salvar
                  </Button>
                </div>
              </div>
            );
          })()}
        </div>
      </Card>

      {/* DB stats */}
      <Card>
        <div className="flex items-center justify-between">
          <Title>PostgreSQL</Title>
          <Database className="h-4 w-4 text-slate-500" />
        </div>
        <div className="mt-3 space-y-2 text-sm text-slate-300">
          <div>
            Versão:{' '}
            <code className="text-xs">
              {i.db.version?.split(' ').slice(0, 2).join(' ') ?? '—'}
            </code>
          </div>
          <div>Tamanho: {fmtBytes(i.db.db_size_bytes)}</div>
          <div className="mt-3 text-slate-400">Tabelas maiores:</div>
          <ul className="ml-4 list-disc text-slate-300">
            {i.db.largest_tables.map((t) => (
              <li key={t.name}>
                <code className="text-blue-300">{t.name}</code> — {t.pretty}
              </li>
            ))}
          </ul>
        </div>
      </Card>

      {/* Feature flags */}
      <Card>
        <Title>Feature Flags</Title>
        <Text>
          Origem: variáveis de ambiente do worker. Mudanças requerem restart.
        </Text>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-slate-400">
              <tr>
                <th className="pb-2">Flag</th>
                <th className="pb-2">Estado</th>
                <th className="pb-2">Descrição</th>
              </tr>
            </thead>
            <tbody>
              {i.feature_flags.map((f) => (
                <tr
                  key={f.name}
                  className="border-t border-slate-800/60 align-top"
                >
                  <td className="py-2 font-mono text-xs text-blue-300">
                    {f.name}
                  </td>
                  <td className="py-2">
                    <Badge color={f.enabled ? 'green' : 'slate'} size="xs">
                      {f.enabled ? 'on' : 'off'}
                    </Badge>
                  </td>
                  <td className="py-2 text-slate-400">{f.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
